import { useState, useEffect } from 'react';
import { Button } from '../../ui/Button';
import { Card, CardHeader, CardTitle } from '../../ui/Card';
import { Input } from '../../ui/Input';
import { Badge } from '../../ui/Badge';
import {
  Plus,
  Trash2,
  HelpCircle,
  Send,
  Clock,
  CheckCircle,
  X,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { useClientQuestions } from '../../../hooks/useClientQuestions';
import { ClientQuestion, QuestionFormData } from '../../../types/questions';
import type { Project } from '../../../types/project';

interface ProjectQuestionsTabProps {
  project: Project;
  clientProfile?: {
    id: string;
    full_name: string;
    email?: string;
  };
}

const QUESTION_TYPES = [
  { id: 'text', label: 'Texte court', icon: '📝' },
  { id: 'textarea', label: 'Texte long', icon: '📄' },
  { id: 'multiple', label: 'Choix multiple', icon: '☑️' },
  { id: 'number', label: 'Nombre', icon: '🔢' },
  { id: 'date', label: 'Date', icon: '📅' }
];

export default function ProjectQuestionsTab({ project, clientProfile }: ProjectQuestionsTabProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [existingQuestions, setExistingQuestions] = useState<any[]>([]);
  const [showExisting, setShowExisting] = useState(true);
  const [selectedQuestionResponses, setSelectedQuestionResponses] = useState<{[key: string]: any[]}>({});
  const [loadingResponses, setLoadingResponses] = useState<{[key: string]: boolean}>({});

  const [formData, setFormData] = useState<QuestionFormData>({
    title: '',
    questions: [{ id: 1, question: '', type: 'text', required: true }],
    expires_in_days: 7
  });

  const {
    loading,
    error,
    createQuestionSet,
    getProjectQuestions,
    getQuestionResponses,
    sendQuestionEmail,
    clearError
  } = useClientQuestions();

  // Charger les questions existantes au montage
  useEffect(() => {
    loadExistingQuestions();
  }, [project.id]);

  // Ajouter une nouvelle question
  const addQuestion = () => {
    const newId = Math.max(...formData.questions.map(q => q.id)) + 1;
    setFormData(prev => ({
      ...prev,
      questions: [...prev.questions, {
        id: newId,
        question: '',
        type: 'text',
        required: true
      }]
    }));
  };

  // Supprimer une question
  const removeQuestion = (id: number) => {
    if (formData.questions.length > 1) {
      setFormData(prev => ({
        ...prev,
        questions: prev.questions.filter(q => q.id !== id)
      }));
    }
  };

  // Mettre à jour une question
  const updateQuestion = (id: number, updates: Partial<ClientQuestion>) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map(q =>
        q.id === id ? { ...q, ...updates } : q
      )
    }));
  };

  // Ajouter/Supprimer une option pour les questions à choix multiple
  const updateQuestionOptions = (questionId: number, options: string[]) => {
    updateQuestion(questionId, { options });
  };

  // Charger les questions existantes
  const loadExistingQuestions = async () => {
    try {
      const questions = await getProjectQuestions(project.id);
      setExistingQuestions(questions);
    } catch (err) {
      console.error('Erreur lors du chargement des questions:', err);
    }
  };

  // Charger les réponses d'une question
  const loadQuestionResponses = async (questionSetId: string) => {
    try {
      setLoadingResponses(prev => ({ ...prev, [questionSetId]: true }));
      const responses = await getQuestionResponses(questionSetId);
      setSelectedQuestionResponses(prev => ({ ...prev, [questionSetId]: responses }));
    } catch (err) {
      console.error('Erreur lors du chargement des réponses:', err);
    } finally {
      setLoadingResponses(prev => ({ ...prev, [questionSetId]: false }));
    }
  };

  // Basculer l'affichage des réponses
  const toggleResponses = (questionSetId: string) => {
    if (selectedQuestionResponses[questionSetId]) {
      // Masquer les réponses
      const newResponses = { ...selectedQuestionResponses };
      delete newResponses[questionSetId];
      setSelectedQuestionResponses(newResponses);
    } else {
      // Charger et afficher les réponses
      loadQuestionResponses(questionSetId);
    }
  };

  // Soumettre les questions
  const handleSubmit = async () => {
    if (!clientProfile?.id || !formData.title.trim() || formData.questions.some(q => !q.question.trim())) {
      return;
    }

    try {
      clearError();

      const questionSet = await createQuestionSet({
        project_id: project.id,
        client_id: clientProfile.id,
        title: formData.title,
        questions: formData.questions,
        expires_in_days: formData.expires_in_days
      });

      // Envoyer l'email au client
      if (clientProfile.email) {
        await sendQuestionEmail(
          questionSet.id,
          clientProfile.email,
          clientProfile.full_name,
          project.title,
          formData.title,
          project.id
        );
      }

      // Reset du formulaire
      setFormData({
        title: '',
        questions: [{ id: 1, question: '', type: 'text', required: true }],
        expires_in_days: 7
      });

      setIsExpanded(false);

      // Rafraîchir la liste
      loadExistingQuestions();
    } catch (err) {
      console.error('Erreur lors de la création des questions:', err);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'answered': return 'bg-green-100 text-green-800';
      case 'expired': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock className="h-4 w-4" />;
      case 'answered': return <CheckCircle className="h-4 w-4" />;
      case 'expired': return <X className="h-4 w-4" />;
      default: return <HelpCircle className="h-4 w-4" />;
    }
  };

  if (!clientProfile?.email) {
    return (
      <div className="mt-6">
        <Card padding="lg">
          <div className="text-center text-gray-500">
            <HelpCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Impossible d'envoyer des questions : aucun email client disponible</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Bouton pour créer de nouvelles questions */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-900">Questions client</h2>
        <Button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Poser des questions
        </Button>
      </div>

      {/* Formulaire de création */}
      {isExpanded && (
        <Card padding="lg">
          <div className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Informations du destinataire */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Destinataire</h4>
              <div className="text-sm text-blue-800 space-y-1">
                <p><span className="font-medium">Client:</span> {clientProfile.full_name}</p>
                <p><span className="font-medium">Email:</span> {clientProfile.email}</p>
                <p><span className="font-medium">Projet:</span> {project.title}</p>
              </div>
            </div>

            {/* Titre des questions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Titre des questions *
              </label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Ex: Questions sur les spécifications du projet"
                disabled={loading}
              />
            </div>

            {/* Durée de validité */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Durée de validité (jours)
              </label>
              <Input
                type="number"
                min="1"
                max="30"
                value={formData.expires_in_days}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  expires_in_days: parseInt(e.target.value) || 7
                }))}
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-1">
                Le lien expirera après ce délai
              </p>
            </div>

            {/* Questions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Questions *
              </label>

              <div className="space-y-4">
                {formData.questions.map((question, index) => (
                  <QuestionEditor
                    key={question.id}
                    question={question}
                    index={index}
                    onUpdate={(updates) => updateQuestion(question.id, updates)}
                    onRemove={() => removeQuestion(question.id)}
                    onUpdateOptions={(options) => updateQuestionOptions(question.id, options)}
                    canRemove={formData.questions.length > 1}
                    disabled={loading}
                  />
                ))}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={addQuestion}
                className="mt-3 flex items-center gap-2"
                disabled={loading}
              >
                <Plus className="h-4 w-4" />
                Ajouter une question
              </Button>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setIsExpanded(false);
                  clearError();
                }}
                disabled={loading}
              >
                Annuler
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading || !formData.title.trim() ||
                         formData.questions.some(q => !q.question.trim())}
                className="flex items-center gap-2"
              >
                <Send className="h-4 w-4" />
                {loading ? 'Envoi...' : 'Envoyer les questions'}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Historique des questions */}
      <div>
        <h3 className="text-base font-semibold text-gray-900 mb-4">
          Historique des questions
        </h3>

        {existingQuestions.length === 0 ? (
          <Card padding="lg">
            <div className="text-center text-gray-500">
              <p className="text-sm">Aucune question posée pour ce projet</p>
            </div>
          </Card>
        ) : (
          <div className="space-y-4">
            {existingQuestions.map((questionSet) => (
              <Card key={questionSet.id} padding="lg">
                <div>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">{questionSet.title}</h4>
                      <p className="text-sm text-gray-600">
                        {questionSet.questions.length} question(s) •
                        Créé le {new Date(questionSet.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(questionSet.status)}
                      <Badge className={getStatusColor(questionSet.status)}>
                        {questionSet.status === 'pending' ? 'En attente' :
                         questionSet.status === 'answered' ? 'Répondu' : 'Expiré'}
                      </Badge>
                    </div>
                  </div>

                  {questionSet.email_sent_at && (
                    <p className="text-xs text-gray-500 mb-3">
                      Email envoyé le {new Date(questionSet.email_sent_at).toLocaleDateString('fr-FR')}
                    </p>
                  )}

                  {/* Bouton pour voir les réponses si la question est répondue */}
                  {questionSet.status === 'answered' && (
                    <div className="mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleResponses(questionSet.id)}
                        disabled={loadingResponses[questionSet.id]}
                        className="flex items-center gap-2"
                      >
                        {loadingResponses[questionSet.id] ? (
                          <>Chargement...</>
                        ) : selectedQuestionResponses[questionSet.id] ? (
                          <><ChevronUp className="h-4 w-4" /> Masquer les réponses</>
                        ) : (
                          <><ChevronDown className="h-4 w-4" /> Voir les réponses</>
                        )}
                      </Button>

                      {/* Affichage des réponses */}
                      {selectedQuestionResponses[questionSet.id] && (
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                          <h5 className="font-medium text-gray-900 mb-3">Réponses du client</h5>
                          {selectedQuestionResponses[questionSet.id].map((response, index) => (
                            <div key={response.id || index} className="mb-4">
                              <p className="text-sm text-gray-500 mb-3">
                                Répondu le {new Date(response.responded_at).toLocaleString('fr-FR')}
                              </p>
                              <div className="space-y-3">
                                {questionSet.questions.map((question: any) => (
                                  <div key={question.id} className="bg-white p-3 rounded border">
                                    <p className="font-medium text-sm text-gray-700 mb-1">
                                      {question.question}
                                    </p>
                                    <p className="text-sm text-gray-900">
                                      {response.responses[question.id] || 'Pas de réponse'}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Composant pour éditer une question individuelle
interface QuestionEditorProps {
  question: ClientQuestion;
  index: number;
  onUpdate: (updates: Partial<ClientQuestion>) => void;
  onRemove: () => void;
  onUpdateOptions: (options: string[]) => void;
  canRemove: boolean;
  disabled: boolean;
}

function QuestionEditor({
  question,
  index,
  onUpdate,
  onRemove,
  onUpdateOptions,
  canRemove,
  disabled
}: QuestionEditorProps) {
  const addOption = () => {
    const currentOptions = question.options || [];
    onUpdateOptions([...currentOptions, '']);
  };

  const updateOption = (optionIndex: number, value: string) => {
    const newOptions = [...(question.options || [])];
    newOptions[optionIndex] = value;
    onUpdateOptions(newOptions);
  };

  const removeOption = (optionIndex: number) => {
    const newOptions = (question.options || []).filter((_, i) => i !== optionIndex);
    onUpdateOptions(newOptions);
  };

  return (
    <div className="bg-gray-50 rounded-lg p-4 space-y-3 border">
      <div className="flex items-start justify-between">
        <span className="text-sm font-medium text-gray-700">
          Question {index + 1}
        </span>
        {canRemove && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRemove}
            disabled={disabled}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Type de question */}
      <div>
        <label className="block text-sm text-gray-600 mb-1">Type</label>
        <select
          value={question.type}
          onChange={(e) => onUpdate({ type: e.target.value as any })}
          disabled={disabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-mint-500 focus:border-mint-500"
        >
          {QUESTION_TYPES.map(type => (
            <option key={type.id} value={type.id}>
              {type.icon} {type.label}
            </option>
          ))}
        </select>
      </div>

      {/* Texte de la question */}
      <div>
        <label className="block text-sm text-gray-600 mb-1">Question</label>
        <Input
          value={question.question}
          onChange={(e) => onUpdate({ question: e.target.value })}
          placeholder="Saisissez votre question..."
          disabled={disabled}
        />
      </div>

      {/* Placeholder (optionnel) */}
      <div>
        <label className="block text-sm text-gray-600 mb-1">
          Indication (optionnel)
        </label>
        <Input
          value={question.placeholder || ''}
          onChange={(e) => onUpdate({ placeholder: e.target.value })}
          placeholder="Ex: Décrivez en quelques mots..."
          disabled={disabled}
        />
      </div>

      {/* Options pour choix multiple */}
      {question.type === 'multiple' && (
        <div>
          <label className="block text-sm text-gray-600 mb-2">Options de réponse</label>
          <div className="space-y-2">
            {(question.options || []).map((option, optionIndex) => (
              <div key={optionIndex} className="flex items-center gap-2">
                <Input
                  value={option}
                  onChange={(e) => updateOption(optionIndex, e.target.value)}
                  placeholder={`Option ${optionIndex + 1}`}
                  disabled={disabled}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => removeOption(optionIndex)}
                  disabled={disabled || (question.options || []).length <= 1}
                  className="text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={addOption}
              disabled={disabled}
              className="flex items-center gap-1"
            >
              <Plus className="h-3 w-3" />
              Ajouter une option
            </Button>
          </div>
        </div>
      )}

      {/* Obligatoire */}
      <div className="flex items-center">
        <input
          type="checkbox"
          id={`required-${question.id}`}
          checked={question.required || false}
          onChange={(e) => onUpdate({ required: e.target.checked })}
          disabled={disabled}
          className="h-4 w-4 text-mint-600 rounded border-gray-300 focus:ring-mint-500"
        />
        <label htmlFor={`required-${question.id}`} className="ml-2 text-sm text-gray-600">
          Réponse obligatoire
        </label>
      </div>
    </div>
  );
}
